import {
  type Component,
  Show,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import AffiliateClaiTip from "@/components/Base/AffiliateClaiTip";
import { Tooltips } from "@/components/Base/Tooltip";
import { DialogType } from "@/components/Dialogs/types.ts";
import { useDialog } from "@/components/Dialogs/useDialog.ts";
import Icon, { svgPathTypes } from "@/components/Icon";
import { useLiveChat } from "@/hooks/livechat/useLiveChat.ts";
import {
  useOnTouchMovellLock,
  useOnWheellLock,
} from "@/hooks/usePreventEventSliding";
import { useRouter } from "@/hooks/useRouter.tsx";
import { useShare } from "@/hooks/useShare";
import { wagerTabList } from "@/hooks/useWagerContest";
import { getLangConf, useI18n } from "@/i18n/index.ts";
import { formatDate } from "@/lib/dateUtils";
import { copyText } from "@/lib/helper";
import { getImgUrlWithSkin, getInstallImg } from "@/lib/resFolder.ts";
import { TOURNAMENT_TRANSLATE } from "@/pages/pc/tournament/constants";
import { sidebarStore } from "@/stores/sidebar.ts";
import { sysConfigStore } from "@/stores/sysConfigStore.ts";
import { userStore } from "@/stores/userStore";
import "@/styles/motion/slide.css";
import { useLocation } from "@solidjs/router";

const { t } = useI18n();
export const Sidebar: Component = () => {
  let interval: ReturnType<typeof setInterval>;

  const location = useLocation();
  const { push } = useRouter();
  const { clickLiveChat } = useLiveChat();
  const { showDialog } = useDialog();
  const { shareLink } = useShare();
  // const [theme, setTheme] = createSignal<"dark" | "light">("dark");
  const [time, setTime] = createSignal(formatDate(new Date().getTime(), 1));
  const isShowWager = createMemo(() => wagerTabList().length > 0);

  onMount(() => {
    interval = setInterval(() => {
      setTime(
        formatDate(
          parseInt(`${Date.now() / 1000}`) - sysConfigStore.timeOffset,
          1,
        ),
      );
    }, 1000);
  });

  onCleanup(() => clearInterval(interval));

  const gameType = createMemo(() => {
    return [
      {
        label: t("home.hot"),
        value: "hotGame",
        icon: "general/like",
        active: location.pathname.includes("hot-game"),
        action: () => push("hot-game"),
      },
      {
        label: t("home.favorites"),
        value: "collections",
        icon: "general/collect",
        active: location.pathname.includes("collections"),
        action: () => push("collections"),
      },
      {
        label: t("home.recent"),
        value: "recentGame",
        icon: "general/history",
        active: location.pathname.includes("recent-game"),
        action: () => push("recent-game"),
      },
    ];
  });

  const menuItems1 = createMemo(() => {
    return [
      {
        icon: "general/casino_big",
        label: t("home.casino"),
        active: location.pathname.includes("casino"),
        action: () => push("casino"),
        hasBg: true,
        isExpandedHidden: true,
        isShow: true,
      },
      {
        icon: "general/sports_big",
        label: t("home.sports"),
        active: location.pathname.includes("sports"),
        action: () =>
          push("sports/" + sysConfigStore.sport_config.sport_game_id),
        hasBg: true,
        isExpandedHidden: true,
        isShow: true,
      },
      {
        //   icon: "general/points",
        //   label: t("home.points_games"),
        //   active: location.pathname.includes("bonus-game"),
        //   action: () => push("bonus-game"),
        //   hasBg: true,
        //   isShow: true,
        // },
        // {
        icon: "general/vip",
        label: t("home.vip_club"),
        active: location.pathname.includes("vip"),
        action: () => push("vip"),
        isShow: true,
      },
      {
        icon: "general/bonus",
        label: t("home.bonus"),
        active: location.pathname.includes("bonus-center"),
        action: () => push("bonus-center"),
        isShow: true,
      },
      // {
      //   icon: "general/championship",
      //   label: t("home.tournament"),
      //   active: location.pathname.includes("bonus-center"),
      //   action: () => push("bonus-center"),
      //   isShow: true,
      // },
      {
        icon: "general/rollover",
        label: t("home.wager_contest"),
        active: location.pathname.includes("wager-contest"),
        action: () => push("wager-contest"),
        isShow: isShowWager(),
      },
      {
        icon: "general/affiliate",
        label: t("home.affiliate"),
        active: location.pathname.includes("affiliate"),
        action: () => push("affiliate"),
        isShow: true,
        showRedDot: userStore.affiliateRewardTip,
      },
      {
        icon: "general/promotions",
        label: t("home.promotions"),
        active: location.pathname.includes("promotion"),
        action: () => {
          push("promotion");
        },
        isShow: true,
      },
      {
        icon: "general/tournament",
        label: t(TOURNAMENT_TRANSLATE.TITLE),
        active: location.pathname.includes("tournament"),
        action: () => push("tournament"),
        isShow: true,
      },
      import.meta.env.VITE_APP_NAME === "br" && {
        icon: "general/sponsor",
        label: t("partner.sponsorship"),
        active: location.pathname.includes("sponsorship"),
        action: () => push("sponsorship"),
        isShow: true,
      },
      // {
      //   icon: "general/Union",
      //   label: t("home.casualgames"),
      //   active: location.pathname.includes("casual-games"),
      //   action: () => push("casual-games"),
      //   isShow: true,
      // },
    ].filter((route) => !!route);
  });

  const navs = createMemo(() => {
    return [
      {
        label: t("help.title"),
        icon: "general/doubt2",
        active: location.pathname.includes("help"),
        action: () => push("help"),
        hasBg: true,
        isAvailable: true,
      },
      {
        label: t("home.feedback"),
        icon: "general/feedback",
        active: location.pathname.includes("feedback"),
        action: () => push("feedback"),
        isAvailable: userStore.isLogin,
      },
      {
        label: t("home.responsible_gambling"),
        icon: "general/safe",
        active: location.pathname.includes("responsible"),
        action: () => push("responsible"),
        isAvailable: true,
      },
      {
        label: t("home.live_support"),
        icon: "general/service",
        active: false, // 在线客服没有路由，所以不会有选中状态
        action: () => clickLiveChat(),
        isAvailable: true,
      },
      {
        label: t("account.language"),
        icon: "general/globe",
        active: false, // 语言切换是弹窗，不会有选中状态
        action: () => showDialog({ type: DialogType.SwitchLanguage }),
        arrow: true,
        value: getLangConf().text,
        isAvailable: true,
      },
    ];
  });

  return (
    <div
      class={`bg-menu-bg2 sidebarSlideBezier flex h-full flex-col overflow-y-auto overflow-x-hidden transition-[width] duration-300 ${sidebarStore.state.isExpanded ? "border-menu-sk w-[240px] border-r-[1px]" : "w-[72px] items-center"}`}
      onWheel={(e) => useOnWheellLock(e)}
      onTouchMove={(e) => useOnTouchMovellLock(e)}
    >
      <div
        class={`flex flex-col gap-[8px] py-[8px] ${sidebarStore.state.isExpanded ? "w-[240px] px-[12px]" : "w-[72px] px-[15px]"}`}
      >
        {/* 代理推广模块 */}
        <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
          <div
            class="relative h-[100px] w-full rounded-[10px] bg-cover pb-[10px] pl-[18px] pt-[29px]"
            style={{
              "background-image": `url(${getImgUrlWithSkin("pc_agent_expanded.png", "/menu")})`,
            }}
            onClick={() => push("/affiliate")}
          >
            <span class="bg-general-main/20 text-menu-light absolute left-0 top-0 min-w-[120px] max-w-[160px] rounded-br-[12px] rounded-tl-[12px] py-[3px] pl-[18.5px] text-[12px] font-[500] leading-[16px]">
              {t("home.referandearn")}
            </span>
            <div class="flex w-full flex-1 flex-col justify-between">
              <div class="mb-[9px] mt-[4px] flex flex-col">
                <h3 class="text-menu-affiliatezi font-Inter max-w-[144px] text-[14px] font-[800] leading-[16px]">
                  {t("home.withdrawrefercash")}
                </h3>
              </div>

              <span
                class="text-general-butzi mt-[1px] flex h-[20px] w-fit min-w-[64px] cursor-pointer items-center rounded-[24px] bg-gradient-to-r from-[var(--general-main)] to-[var(--general-assist)] px-[8px] text-[10px]"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!userStore.isLogin) {
                    push("/register");
                    return;
                  }
                  copyText(shareLink());
                }}
              >
                {t("home.copy_link")}
              </span>
            </div>
          </div>
        </Show>
        {/* 代理推广模块 */}
        <Show when={!sidebarStore.state.isExpanded} fallback={<></>}>
          <div>
            <img
              src={getImgUrlWithSkin("pc_agent_no_expanded.png", "/menu")}
              alt="pc_agent_no_expanded icon"
              class="h-[50px] w-full rounded-[10px]"
            />
          </div>
        </Show>
        <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
          <div class="flex w-full justify-between gap-1">
            <div class="flex w-full gap-[5px]">
              {gameType().map((item) => (
                <button
                  class={`bg-menu-bg1 hover:from-general-main20 hover:to-general-main0 group flex h-full w-full flex-1 cursor-pointer flex-col items-center justify-center gap-[2px] rounded-[8px] py-[6px] text-center hover:bg-gradient-to-r ${
                    item.active
                      ? "from-general-main20 to-general-main0 bg-gradient-to-r"
                      : ""
                  }`}
                  onclick={item.action}
                >
                  <Icon
                    name={item.icon as svgPathTypes}
                    class={`size-[20px] ${
                      item.active
                        ? "text-general-light"
                        : "text-menu-icon group-hover:text-menu-light"
                    }`}
                  />
                  <span
                    class={`text-menu-darkzi text-[12px] font-[500] leading-[16px] ${
                      item.active ? "text-general-light" : ""
                    }`}
                  >
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Show>
        {/* 主导航菜单 */}
        <div
          class="flex flex-col"
          classList={{
            "gap-[4px]": sidebarStore.state.isExpanded,
            "gap-[0px]": sidebarStore.state.isExpanded,
          }}
        >
          {menuItems1()
            ?.filter(
              (item) =>
                (!sidebarStore.state.isExpanded || !item.isExpandedHidden) &&
                item.isShow,
            )
            .map((item, index) => {
              return (
                <div
                  class={`hover:from-general-main20 text-menu-darkzi hover:text-general-light hover:to-general-main0 group flex w-full cursor-pointer items-center gap-[8px] rounded-[8px] hover:bg-gradient-to-r ${
                    sidebarStore.state.isExpanded
                      ? "pr-[7px h-[40px] pl-[12px]"
                      : "h-[42px]"
                  } ${item.active ? "from-general-main20 to-general-main0 bg-gradient-to-r" : ""} ${
                    item.hasBg && !sidebarStore.state.isExpanded
                      ? "bg-menu-bg6"
                      : ""
                  }`}
                  classList={{
                    // 前三个有不一样的样式需要间隔
                    "mb-[6px]": !sidebarStore.state.isExpanded && index <= 2,
                  }}
                  onclick={item.action}
                >
                  <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
                    <Icon
                      name={item.icon as svgPathTypes}
                      class={`size-[20px] ${
                        item.active
                          ? "text-general-light"
                          : "text-menu-icon group-hover:text-menu-light"
                      }`}
                      wrapperClass={`${
                        sidebarStore.state.isExpanded
                          ? "w-[20px] h-[20px]"
                          : "w-[42px] h-[42px]"
                      }`}
                    />
                    <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
                      <span
                        class={`text-sm font-[600] leading-4 ${
                          item.active ? "text-general-light" : ""
                        }`}
                      >
                        {item.label.toLowerCase().includes("vip") ? (
                          <div>
                            {item.label.split(/(vip)/i).map((part, index) => (
                              <span
                                class={
                                  part.toLowerCase() === "vip"
                                    ? "text-general-main"
                                    : ""
                                }
                              >
                                {part}
                              </span>
                            ))}
                          </div>
                        ) : (
                          item.label
                        )}
                      </span>
                    </Show>
                  </Show>
                  {/* 因为前三个要有 mb-[6px], 会让 Tooltips 不置中 */}
                  <Show when={!sidebarStore.state.isExpanded} fallback={<></>}>
                    <Tooltips
                      class="flex items-center gap-[8px]"
                      content={item.label}
                      placement="right"
                      showArrow
                      contentClass="w-fit h-[35px] rounded-4px bg-menu-bg7 px-[7px] text-[14px] font-[600] leading-[20px] text-menu-light"
                      arrowColor="text-menu-bg7"
                      offset={{ x: 4 }}
                      stopPropagation={false}
                    >
                      <Icon
                        name={item.icon as svgPathTypes}
                        class={`size-[20px] ${
                          item.active
                            ? "text-general-light"
                            : "text-menu-icon group-hover:text-menu-light"
                        }`}
                        wrapperClass={`${
                          sidebarStore.state.isExpanded
                            ? "w-[20px] h-[20px]"
                            : "w-[42px] h-[42px]"
                        }`}
                      />
                    </Tooltips>
                  </Show>
                  <Show when={item.showRedDot} fallback={<></>}>
                    <AffiliateClaiTip
                      class={
                        sidebarStore.state.isExpanded
                          ? "right-[-6px] top-1/2 -translate-y-1/2"
                          : "left-[-14px] top-[6px]"
                      }
                    />
                  </Show>
                </div>
              );
            })}
        </div>

        {/* 下载APP */}
        <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
          <div
            class="border-menu-sk relative min-h-[70px] w-[216px] rounded-[8px] border-[1px] bg-cover bg-no-repeat px-[13px] py-[10.5px]"
            style={{
              "background-clip": "padding-box",
              "background-image": `url(${getInstallImg("dwnload_bar_pc.png")})`,
            }}
            onclick={() => push("/install")}
          >
            <div class="text-general-light font-Inter flex max-w-[140px] cursor-pointer flex-col">
              <span class="text-[14px] font-[700] leading-[17px]">
                {t("home.download_app")}
              </span>
              <span class="text-menu-darkzi text-[12px] font-[500] leading-[15px]">
                {t("home.exclusive_bonus")}
              </span>
            </div>
          </div>
        </Show>

        <div
          class="flex flex-col"
          classList={{
            "gap-[4px]": sidebarStore.state.isExpanded,
            "gap-[0px]": sidebarStore.state.isExpanded,
          }}
        >
          {navs()
            .filter((item) => item.isAvailable)
            .map((item) => {
              return (
                <div
                  class={`hover:from-general-main20 text-menu-darkzi hover:text-general-light hover:to-general-main0 group flex w-full cursor-pointer items-center gap-[8px] rounded-[8px] hover:bg-gradient-to-r ${
                    sidebarStore.state.isExpanded
                      ? "pr-[7px h-[40px] pl-[12px]"
                      : "h-[42px] justify-center"
                  } ${item.active ? "from-general-main20 to-general-main0 bg-gradient-to-r" : ""} ${
                    item.hasBg && !sidebarStore.state.isExpanded
                      ? "bg-menu-bg6"
                      : ""
                  }`}
                  onclick={item.action}
                >
                  <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
                    <Icon
                      name={item.icon as svgPathTypes}
                      class={`size-[20px] ${
                        item.active
                          ? "text-general-light"
                          : "text-menu-icon group-hover:text-menu-light"
                      }`}
                      wrapperClass={`${
                        sidebarStore.state.isExpanded
                          ? "w-[20px] h-[20px]"
                          : "w-[42px] h-[42px]"
                      }`}
                    />
                    <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
                      <span
                        class={`text-sm font-[600] leading-4 ${
                          item.active ? "text-general-light" : ""
                        }`}
                      >
                        {item.label}
                      </span>
                    </Show>
                  </Show>
                  {/* 因为前三个要有 mb-[6px], 会让 Tooltips 不置中 */}
                  <Show when={!sidebarStore.state.isExpanded} fallback={<></>}>
                    <Tooltips
                      class="flex items-center gap-[8px]"
                      content={item.label}
                      placement="right"
                      showArrow
                      contentClass="w-fit h-[35px] rounded-4px bg-menu-bg7 px-[7px] text-[14px] font-[600] leading-[20px] text-menu-light"
                      arrowColor="text-menu-bg7"
                      offset={{ x: 4 }}
                      stopPropagation={false}
                    >
                      <Icon
                        name={item.icon as svgPathTypes}
                        class={`size-[20px] ${
                          item.active
                            ? "text-general-light"
                            : "text-menu-icon group-hover:text-menu-light"
                        }`}
                        wrapperClass={`${
                          sidebarStore.state.isExpanded
                            ? "w-[20px] h-[20px]"
                            : "w-[42px] h-[42px]"
                        }`}
                      />
                    </Tooltips>
                  </Show>
                </div>
              );
            })}
        </div>

        <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
          {/* 分割线 */}
          <div class="bg-menu-bg6 h-[1px] w-full" />

          {/* 时间 */}
          <div class="flex h-[40px] items-center gap-[8px] px-[12px]">
            <Icon name="general/clock" class="h-[20px] w-[20px]" />
            <span class="text-menu-darkzi text-[14px] font-[600] leading-[17px]">
              {time()}
            </span>
          </div>

          {/* 主题切换 - 暂时隐藏 */}
          {/* <div class="border-menu-sk flex h-[40px] cursor-pointer items-center rounded-md border-[1px]">
          <div
            class="flex h-[40px] flex-1 items-center justify-center gap-[5px] rounded-[8px]"
            classList={{
              "bg-menu-bg6 text-general-light": theme() === "dark",
              "text-general-darkzi": theme() !== "dark",
            }}
            onclick={() => setTheme("dark")}
          >
            <Icon
              name="general/night"
              class={`h-[16px] w-[16px] ${
                theme() === "dark" ? "text-general-light" : "text-general-icon"
              }`}
            />
            <span class="text-[14px] font-[600] leading-[17px]">
              {t("home.dark")}
            </span>
          </div>
          <div
            class="flex h-[40px] flex-1 items-center justify-center gap-[5px] rounded-[8px]"
            classList={{
              "bg-menu-bg6 text-general-light": theme() === "light",
              "text-general-darkzi": theme() !== "light",
            }}
            onclick={() => setTheme("light")}
          >
            <Icon
              name="general/daytime"
              class={`h-[16px] w-[16px] ${
                theme() === "light" ? "text-general-light" : "text-general-icon"
              }`}
            />
            <span class="text-[14px] font-[600] leading-[17px]">
              {t("home.light")}
            </span>
          </div>
        </div> */}
        </Show>
        {/* 支付方式菜单 */}
        {/* <Show when={sidebarStore.state.isExpanded} fallback={<></>}>
        <BuyCrypto />
      </Show> */}
      </div>
    </div>
  );
};
